import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { ArrowLeft, BookOpen, Save, Share2, Printer } from 'lucide-react';
import { toast } from 'sonner';

export default function HomeworkPartnerSchedule() {
  const navigate = useNavigate();
  const printPage = () => { window.print(); toast.success('Print dialog opened'); };
  
  const [schedule, setSchedule] = useState(() => {
    const saved = localStorage.getItem('homeworkPartnerSchedule');
    return saved ? JSON.parse(saved) : Array(7).fill(null).map((_, i) => ({ week: i + 1, name: '', when: '', where: '' }));
  });

  const saveProgress = () => {
    localStorage.setItem('homeworkPartnerSchedule', JSON.stringify(schedule));
    toast.success('Schedule saved!');
  };

  const shareSchedule = () => {
    const scheduleText = schedule.map(w => `Week ${w.week}: ${w.name || '(empty)'} - ${w.when || '(empty)'} at ${w.where || '(empty)'}`).join('\n');
    navigator.clipboard.writeText(scheduleText);
    toast.success('Schedule copied to clipboard!');
  };

  const updateWeek = (idx, field, value) => {
    const updated = [...schedule];
    updated[idx][field] = value;
    setSchedule(updated);
  };

  useEffect(() => {
    const interval = setInterval(() => localStorage.setItem('homeworkPartnerSchedule', JSON.stringify(schedule)), 30000);
    return () => clearInterval(interval);
  }, [schedule]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <style>{`@media print { body { background: white !important; } .no-print { display: none !important; } .print-page { background: white !important; color: black !important; } }`}</style>
      <div className="border-b border-slate-700/50 bg-slate-900/90 backdrop-blur-sm sticky top-0 z-50 no-print">
        <div className="container mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button onClick={() => navigate('/belonging-ebook')} variant="outline" size="sm" className="border-slate-600 hover:border-slate-500 text-white hover:bg-slate-800">
                <ArrowLeft className="h-4 w-4 mr-2" />Back to Guide
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-white">Weekly Homework Partner Schedule</h1>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button onClick={shareSchedule} variant="outline" size="sm" className="border-purple-600 hover:bg-purple-900/20 text-white">
                <Share2 className="h-4 w-4 mr-2" />Share
              </Button>
              <Button onClick={saveProgress} variant="outline" size="sm" className="border-green-600 hover:bg-green-900/20 text-white">
                <Save className="h-4 w-4 mr-2" />Save
              </Button>
              <Button onClick={printPage} variant="outline" size="sm" className="border-blue-600 hover:bg-blue-900/20 text-white">
                <Printer className="h-4 w-4 mr-2" />Print
              </Button>
              <div className="flex items-center gap-2 text-slate-400 text-sm"><BookOpen className="h-4 w-4" /><span>Page 78</span></div>
            </div>
          </div>
        </div>
      </div>
      <div className="container mx-auto px-6 py-12 print-page">
        <div className="max-w-5xl mx-auto">
          <div className="bg-blue-900/20 border border-blue-700/30 rounded-lg p-6 mb-8">
            <p className="text-blue-100 text-center">This page can be used to arrange times to get together with classmates for homework practice and community building.</p>
          </div>
          <div className="bg-slate-800/40 border border-slate-700/50 rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-900/60">
                <tr>
                  <th className="px-4 py-3 text-left text-white font-semibold">Week</th>
                  <th className="px-4 py-3 text-left text-white font-semibold">Name & Contact Info</th>
                  <th className="px-4 py-3 text-left text-white font-semibold">When we will meet?</th>
                  <th className="px-4 py-3 text-left text-white font-semibold">Where we will meet?</th>
                </tr>
              </thead>
              <tbody>
                {schedule.map((week, idx) => (
                  <tr key={idx} className="border-t border-slate-700/30">
                    <td className="px-4 py-3 text-white font-semibold">Week {week.week}</td>
                    <td className="px-4 py-3">
                      <Input value={week.name} onChange={(e) => updateWeek(idx, 'name', e.target.value)} placeholder="Partner name & phone/email" className="bg-slate-900/40 border-slate-700 text-white" />
                    </td>
                    <td className="px-4 py-3">
                      <Input value={week.when} onChange={(e) => updateWeek(idx, 'when', e.target.value)} placeholder="Day/time" className="bg-slate-900/40 border-slate-700 text-white" />
                    </td>
                    <td className="px-4 py-3">
                      <Input value={week.where} onChange={(e) => updateWeek(idx, 'where', e.target.value)} placeholder="Location/Zoom link" className="bg-slate-900/40 border-slate-700 text-white" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-center text-slate-400 text-sm mt-6">💾 Auto-saves every 30 seconds</p>
        </div>
      </div>
    </div>
  );
}
